import { Box, Button, Card, Chip, CircularProgress, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useTeamPageLayoutContext } from "../..";
import { AvatarGroup, MeetingNotesDrawerContent, SignalRGroup, useDrawer } from "../../../../components";
import { Links } from "../../../../constants";
import { useSignalR } from "../../../../hooks";
import { queryKeys, TeamMeetingDto, useJoinMeeting } from "../../../../http";

interface IProps {
  meeting: TeamMeetingDto;
  onSettingsClicked?: (meetingGuid: string) => void;
}

const TeamMeetingListItem = (props: IProps) => {
  const { team } = useTeamPageLayoutContext();
  const { mutate: joinMeeting, isLoading: isJoiningMeeting } = useJoinMeeting();
  const { showDrawer } = useDrawer();
  const theme = useTheme();
  const navigate = useNavigate();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  const inProgress = props.meeting.currentInstance != null;
  const lastInstance =
    props.meeting.lastInstanceDateTimeUtc != null
      ? DateTime.fromISO(props.meeting.lastInstanceDateTimeUtc, { zone: "utc" })
          .toLocal()
          .toLocaleString(DateTime.DATE_MED)
      : null;

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.MeetingListItem(props.meeting.guid));
    return () => signalR.leaveGroup(SignalRGroup.MeetingListItem(props.meeting.guid));
  }, [props.meeting.guid, signalR]);

  useEffect(() => {
    const handleShouldRefetchMeetingListItem = () => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(team.guid));
    };

    signalR.onShouldRefetchMeetingListItem(handleShouldRefetchMeetingListItem);

    return () => signalR.offShouldRefetchMeetingListItem(handleShouldRefetchMeetingListItem);
  }, [queryClient, signalR, team]);

  function handleMeetingNotesClicked(meetingGuid: string) {
    showDrawer({
      content: (
        <MeetingNotesDrawerContent
          title={
            <Stack>
              <Typography variant="h6">Meeting Notes</Typography>
              <Typography variant="caption">Meeting Notes are accessible to all meeting participants.</Typography>
            </Stack>
          }
          meetingGuid={meetingGuid}
        />
      ),
    });
  }

  return (
    <Card>
      <Stack spacing={0.5} sx={{ p: 1, backgroundColor: inProgress ? theme.palette.primary.opacity5 : undefined }}>
        {inProgress && <Chip color="primary" label="In Progress" />}
        <Box>
          <Typography variant="h5">{props.meeting.title}</Typography>
          {props.meeting.description && <Typography>{props.meeting.description}</Typography>}
          <Stack
            direction="row"
            spacing={0.25}
            divider={<Divider flexItem orientation="vertical" sx={{ height: 12, alignSelf: "center" }} />}
            sx={{ alignItems: "center" }}
          >
            <Typography variant="caption">{props.meeting.instancesCount} Occurrences</Typography>
            {lastInstance != null && <Typography variant="caption">Last Meeting {lastInstance}</Typography>}
            {props.meeting.canEdit && (
              <Button
                variant="text"
                size="small"
                sx={{ minWidth: 0 }}
                disableRipple
                disabled={props.meeting.currentInstance != null || isJoiningMeeting}
                onClick={() => props.onSettingsClicked?.(props.meeting.guid)}
              >
                Settings
              </Button>
            )}
            {props.meeting.isInvited && (
              <Button
                variant="text"
                size="small"
                disableRipple
                onClick={() => handleMeetingNotesClicked(props.meeting.guid)}
              >
                Meeting Notes
              </Button>
            )}
          </Stack>
        </Box>

        {props.meeting.currentInstance != null && (
          <AvatarGroup users={props.meeting.currentInstance.currentParticipants} max={2} />
        )}

        {props.meeting.isInvited && (
          <Button
            variant="tertiary"
            disabled={isJoiningMeeting}
            endIcon={isJoiningMeeting ? <CircularProgress size={14} sx={{ color: "grey.400" }} /> : null}
            sx={{ alignSelf: "start" }}
            onClick={() => {
              joinMeeting(
                {
                  meetingGuid: props.meeting.guid,
                },
                {
                  onSuccess: () => {
                    signalR.sendRefetchMeetingListItem(props.meeting.guid);
                    signalR.sendRefetchMeetingParticipants(props.meeting.guid);
                    navigate(Links.Meeting(props.meeting.guid));
                  },
                },
              );
            }}
          >
            {inProgress ? "Join Meeting" : "Start Meeting"}
          </Button>
        )}
      </Stack>
    </Card>
  );
};

export { TeamMeetingListItem };
